import React, { useEffect, useRef, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import { toast } from "sonner";

let sdkPromise = null;
const loadSdk = (clientId) => {
  if (window.paypal && sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=EUR&intent=capture&locale=it_IT&components=buttons&disable-funding=credit`;
    s.onload = () => resolve(window.paypal);
    s.onerror = () => { sdkPromise = null; reject(new Error("PayPal non caricato")); };
    document.body.appendChild(s);
  });
  return sdkPromise;
};

export const usePayPalConfig = () => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/paypal/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })); }, []);
  return cfg;
};

/** kind: order | contract | quote — refId: id del documento da pagare. onPaid(result) dopo la cattura. */
export const PayPalButton = ({ kind, refId, onPaid, compact = false }) => {
  const cfg = usePayPalConfig();
  const box = useRef(null);
  const cb = useRef({ onPaid });
  cb.current.onPaid = onPaid;
  const [state, setState] = useState("loading");
  useEffect(() => {
    if (!cfg) return undefined;
    if (!cfg.enabled) { setState("off"); return undefined; }
    let dead = false, buttons = null;
    loadSdk(cfg.client_id).then((paypal) => {
      if (dead || !box.current) return;
      box.current.innerHTML = "";
      buttons = paypal.Buttons({
        style: { layout: "vertical", color: "gold", shape: "pill", label: "pay", height: compact ? 40 : 48 },
        createOrder: async () => {
          try { const { data } = await api.post("/paypal/orders", { kind, ref_id: refId }); return data.id; }
          catch (e) { toast.error(errMsg(e, "Impossibile avviare il pagamento PayPal")); throw e; }
        },
        onApprove: async (data) => {
          setState("capturing");
          try {
            const { data: r } = await api.post(`/paypal/orders/${data.orderID}/capture`);
            if (r.payment_status === "paid") { toast.success("Pagamento PayPal completato"); setState("paid"); cb.current.onPaid?.(r); }
            else { toast.info("Pagamento in verifica da parte di PayPal: ti confermeremo via email."); setState("pending"); }
          } catch (e) { toast.error(errMsg(e, "Pagamento non completato")); setState("ready"); }
        },
        onCancel: () => toast.info("Pagamento PayPal annullato"),
        onError: () => { toast.error("Errore PayPal, riprova"); setState("ready"); },
      });
      if (buttons.isEligible()) buttons.render(box.current).then(() => !dead && setState("ready")).catch(() => !dead && setState("error"));
      else setState("error");
    }).catch(() => !dead && setState("error"));
    return () => { dead = true; try { buttons?.close(); } catch { /* noop */ } };
  }, [cfg, kind, refId, compact]);
  if (state === "off") return <p className="text-[13px] text-[#56585e]" data-testid="paypal-off">PayPal non è attivo al momento.</p>;
  if (state === "paid") return <p className="text-[14px] font-bold text-[#1a8754] flex items-center gap-2" data-testid="paypal-paid"><ShieldCheck className="w-5 h-5" /> Pagato con PayPal</p>;
  return (
    <div data-testid={`paypal-${kind}-${refId}`} className="relative">
      {(state === "loading" || state === "capturing") && <div className="flex items-center gap-2 text-[13px] text-[#56585e] py-2" data-testid="paypal-loading"><Loader2 className="w-4 h-4 animate-spin" />{state === "capturing" ? "Conferma del pagamento in corso..." : "Caricamento PayPal..."}</div>}
      {state === "error" && <p className="text-[13px] text-red-600" data-testid="paypal-error">PayPal non disponibile su questo browser. Riprova o scegli un altro metodo.</p>}
      {state === "pending" && <p className="text-[13px] text-[#1A237E]" data-testid="paypal-pending">Pagamento in verifica: riceverai conferma via email.</p>}
      <div ref={box} className={state === "capturing" ? "pointer-events-none opacity-50" : ""} />
      {cfg?.mode === "sandbox" && state === "ready" && <p className="text-[11px] text-[#9aa0a6] mt-1 text-center" data-testid="paypal-sandbox">Ambiente di prova PayPal (sandbox)</p>}
    </div>
  );
};

export default PayPalButton;
